/**
 * Bidirectional text helpers.
 *
 * The landing already paid for this lesson, and its stylesheet records it:
 * forcing `direction: ltr` on mixed strings silently reversed things like
 * "₪1,200 ומעלה" and "רונית · 00:42:13". Isolation is what mixed content needs;
 * direction is only correct when the content is genuinely Latin-first.
 *
 * These helpers return the classification, and the UI layer applies it — either
 * a <bdi> element or the `.ltr-inline` class. Keeping the decision here means
 * one place defines what counts as Latin-first.
 */
export type TextDirectionHint = "isolate" | "ltr";

/**
 * Values that are always Latin-first and safe to render LTR: emails, URLs,
 * E.164 phone numbers, tax file numbers, reference codes.
 */
export function directionHintFor(kind: FieldKind): TextDirectionHint {
  return LATIN_FIRST_FIELDS.has(kind) ? "ltr" : "isolate";
}

export type FieldKind =
  | "email"
  | "url"
  | "phone"
  | "tax_file_number"
  | "reference"
  | "money"
  | "duration"
  | "date"
  | "person_name"
  | "business_name"
  | "free_text";

const LATIN_FIRST_FIELDS = new Set<FieldKind>([
  "email",
  "url",
  "phone",
  "tax_file_number",
  "reference",
]);

/**
 * Israeli phone numbers for display: 05X-XXX-XXXX locally, +972 on the wire.
 * WhatsApp only accepts E.164, so storage is always normalized and display is
 * always derived.
 */
export function formatIsraeliPhone(e164: string): string {
  const digits = e164.replace(/\D/g, "");
  const national = digits.startsWith("972") ? `0${digits.slice(3)}` : digits;

  if (national.length === 10 && national.startsWith("05")) {
    return `${national.slice(0, 3)}-${national.slice(3, 6)}-${national.slice(6)}`;
  }
  if (national.length === 9) {
    return `${national.slice(0, 2)}-${national.slice(2, 5)}-${national.slice(5)}`;
  }
  return national;
}

export function toE164Israeli(input: string): string | null {
  const digits = input.replace(/\D/g, "");
  if (digits.startsWith("972")) return `+${digits}`;
  if (digits.startsWith("0")) return `+972${digits.slice(1)}`;
  if (digits.length === 9) return `+972${digits}`;
  return null;
}

/**
 * Israeli ID / company number check digit (Luhn-like, weights 1 and 2 over nine
 * digits). Validating on entry is cheap and prevents a whole class of filing
 * rejections that would otherwise surface only at the tax authority.
 */
export function isValidIsraeliIdNumber(value: string): boolean {
  const digits = value.replace(/\D/g, "").padStart(9, "0");
  if (digits.length !== 9) return false;

  let sum = 0;
  for (let i = 0; i < 9; i += 1) {
    const digit = Number(digits[i]);
    const weighted = digit * ((i % 2) + 1);
    sum += weighted > 9 ? weighted - 9 : weighted;
  }
  return sum % 10 === 0;
}
